import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Users, UserCircle, Calendar, Radio, TrendingUp, Clock, Shield, Crown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import AdminSidebar from '@/components/layout/AdminSidebar';
import axios from 'axios';
import { API } from '@/App';

export default function AdminDashboard({ user, setUser }) {
  const [stats, setStats] = useState(null);
  const [recentMatches, setRecentMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const [statsRes, matchesRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers }),
          axios.get(`${API}/admin/matches`, { headers })
        ]);
        setStats(statsRes.data);
        setRecentMatches(matchesRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    { title: 'Leagues', value: stats?.leagues_count || 0, icon: Trophy, color: 'teal', link: '/admin/leagues' },
    { title: 'Teams', value: stats?.teams_count || 0, icon: Users, color: 'red', link: '/admin/teams' },
    { title: 'Players', value: stats?.players_count || 0, icon: UserCircle, color: 'purple', link: '/admin/players' },
    { title: 'Matches', value: stats?.matches_count || 0, icon: Calendar, color: 'green', link: '/admin/matches' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminSidebar user={user} setUser={setUser} />
      
      <div className="lg:ml-64 pt-14 lg:pt-0">
        <div className="p-6">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
              {user?.admin_type === 'super_admin' ? (
                <Badge className="bg-[#8B1E3F] text-white border-none">
                  <Crown className="w-3 h-3 mr-1" /> Super Admin
                </Badge>
              ) : (
                <Badge className="bg-[#2D5A5A] text-white border-none">
                  <Shield className="w-3 h-3 mr-1" /> League Admin
                </Badge>
              )}
            </div>
            <p className="text-slate-500 mt-1">Welcome back, {user?.name?.split(' ')[0]}</p>
            {user?.admin_type !== 'super_admin' && (
              <p className="text-sm text-slate-400 mt-1">You can manage leagues assigned to you</p>
            )}
          </div>

          {/* Stats Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {statCards.map((stat) => {
              const Icon = stat.icon;
              const bgColors = {
                teal: 'bg-[#C5D5D5]',
                red: 'bg-red-100',
                purple: 'bg-purple-100',
                green: 'bg-green-100'
              };
              const textColors = {
                teal: 'text-[#2D5A5A]',
                red: 'text-[#8B1E3F]',
                purple: 'text-purple-600',
                green: 'text-green-600'
              };
              return (
                <Link key={stat.title} to={stat.link}>
                  <Card className="card-base cursor-pointer h-full" data-testid={`stat-${stat.title.toLowerCase()}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm text-slate-500 font-medium">{stat.title}</p>
                          <p className={`font-stats text-3xl mt-1 ${textColors[stat.color]}`}>
                            {loading ? '...' : stat.value}
                          </p>
                        </div>
                        <div className={`w-10 h-10 rounded-xl ${bgColors[stat.color]} flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 ${textColors[stat.color]}`} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          {/* Match Status */}
          <div className="grid lg:grid-cols-3 gap-4 mb-8">
            <Card className="card-base">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Scheduled</p>
                  <p className="font-stats text-xl text-blue-600">{stats?.matches?.scheduled || 0}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="card-base">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                  <Radio className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Live</p>
                  <p className="font-stats text-xl text-red-600">{stats?.matches?.live || 0}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="card-base">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Completed</p>
                  <p className="font-stats text-xl text-slate-600">{stats?.matches?.completed || 0}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions & Recent */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to="/admin/leagues" className="block">
                  <div className="flex items-center gap-3 p-4 rounded-xl hover:bg-slate-50 border border-slate-100">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Trophy className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Create League</p>
                      <p className="text-sm text-slate-500">Set up a new cricket league</p>
                    </div>
                  </div>
                </Link>
                <Link to="/admin/matches" className="block">
                  <div className="flex items-center gap-3 p-4 rounded-xl hover:bg-slate-50 border border-slate-100">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Schedule Match</p>
                      <p className="text-sm text-slate-500">Add a new match</p>
                    </div>
                  </div>
                </Link>
                <Link to="/admin/users" className="block">
                  <div className="flex items-center gap-3 p-4 rounded-xl hover:bg-slate-50 border border-slate-100">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <UserCircle className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Manage Users</p>
                      <p className="text-sm text-slate-500">Assign roles</p>
                    </div>
                  </div>
                </Link>
              </CardContent>
            </Card>

            <Card className="card-base">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Recent Matches</CardTitle>
                <Link to="/admin/matches" className="text-sm text-blue-600 hover:underline">View All</Link>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => <div key={i} className="skeleton h-14 rounded-xl"></div>)}
                  </div>
                ) : recentMatches.length > 0 ? (
                  <div className="space-y-2">
                    {recentMatches.map((match) => (
                      <Link key={match.match_id} to={`/matches/${match.match_id}`}>
                        <div className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 border border-slate-100">
                          <div>
                            <p className="font-medium text-slate-900 text-sm">Match</p>
                            <p className="text-xs text-slate-500">{match.ground}</p>
                          </div>
                          <Badge className={
                            match.status === 'live' ? 'badge-live' :
                            match.status === 'scheduled' ? 'badge-scheduled' :
                            'badge-completed'
                          }>
                            {match.status}
                          </Badge>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">No matches yet</div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
